// UsersManagement.tsx

import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './UsersManagement.module.css';
import more from '../Images/more-vertical.svg';
import useOnClickOutside from '../hooks/useOnClickOutside.ts';
import Modal from '../Components/Modal.tsx';
import { MdOutlineDeleteOutline } from 'react-icons/md';
import { CiEdit } from 'react-icons/ci';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { FiSearch, FiX } from 'react-icons/fi';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import Spinner from '../Components/Spinner.tsx';
import { useUsers } from '../hooks/useUsers.ts';
import Pagination from '../Components/Pagination.tsx';
import { useTheme } from '../hooks/useTheme.ts';

interface Organization {
  id: number;
  name: string;
}

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
  organizations: Organization[];
}

type SortKey = 'name' | 'email' | 'organization' | 'role';
type SortDir = 'asc' | 'desc';

const getData = function (users: User[] | undefined, page: number, limit: number) {
  if (users === undefined) {
    return [];
  }
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  return users.slice(startIndex, endIndex);
};

function getRole(role: string): string {
  if (!role) {
    return 'Not defined';
  }
  const rolesObject: { [key: string]: string } = {
    Admin: 'Admin',
    User: 'User',
    Viewer: 'Viewer',
    Editor: 'Editor',
    'Sensor provider': 'Sensor Provider',
    Manager: 'Manager',
  };

  return rolesObject[role] || role;
}

function getOrgNames(user: User): string {
  return user.organizations && user.organizations.length > 0
    ? user.organizations.map((org) => org.name).join(', ')
    : '';
}

function getSortValue(user: User, key: SortKey): string {
  switch (key) {
    case 'name':
      return `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim().toLowerCase();
    case 'email':
      return (user.email ?? '').toLowerCase();
    case 'organization':
      return getOrgNames(user).toLowerCase();
    case 'role':
      return (user.roles && user.roles.length > 0 ? user.roles[0] : '').toLowerCase();
    default:
      return '';
  }
}

function UsersManagement() {
  const { isLoading, allUsers, error } = useUsers();
  const { theme } = useTheme();
  const isDarkTheme = theme === 'dark';
  const [currentPage, setCurrentPage] = useState(1);
  const limitOfUsers = 10;
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const popUpMenuRef = useRef<HTMLDivElement>(null);

  const clickOutSideHandler = () => {
    setShowModal(false);
  };

  useOnClickOutside(popUpMenuRef, clickOutSideHandler);

  const handleEdit = (id: string) => {
    setSelectedItem(id);
    setOpen(true);
  };

  const handleDelete = (id: string) => {
    setSelectedItem(id);
    setOpenConfirm(true);
  };

  const handleActionsClick = (id: string) => {
    setShowModal(!showModal);
    setSelectedItem(id);
  };

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // Filter + sort before paginating, so page 1 always shows the best matches.
  const visibleUsers = useMemo(() => {
    const list = (allUsers ?? []).filter((user: User) => user !== undefined);
    const query = search.trim().toLowerCase();

    const filtered = query
      ? list.filter((user: User) =>
          [
            `${user.first_name ?? ''} ${user.last_name ?? ''}`,
            user.email ?? '',
            getOrgNames(user),
            user.roles && user.roles.length > 0 ? getRole(user.roles[0]) : '',
          ]
            .join(' ')
            .toLowerCase()
            .includes(query)
        )
      : list;

    return [...filtered].sort((a: User, b: User) => {
      const cmp = getSortValue(a, sortKey).localeCompare(getSortValue(b, sortKey), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allUsers, search, sortKey, sortDir]);

  // Any change to the result set puts you back on page 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortKey, sortDir]);

  const sortIcon = (key: SortKey) => {
    if (key !== sortKey) return <FaSort className={styles.sortIcon} />;
    return sortDir === 'asc' ? (
      <FaSortUp className={styles.sortIconActive} />
    ) : (
      <FaSortDown className={styles.sortIconActive} />
    );
  };

  const renderUsers = () => {
    return getData(visibleUsers, currentPage, limitOfUsers).map((user) => {
      return (
        <tr key={user.email}>
          <td>
            <div className={styles.avatar}>{`${user.first_name} ${user.last_name}`}</div>
          </td>
          <td>{user.email}</td>
          <td>{getOrgNames(user) || '-'}</td>
          <td className={styles.role_info}>
            <div
              className={`${styles.role} ${
                user.roles && user.roles.length > 0
                  ? styles[`role_${user.roles[0].toLowerCase()}`]
                  : ''
              }`}
            >
              {user.roles && user.roles.length > 0 ? getRole(user.roles[0]) : 'No Role'}
            </div>
            <div className={styles.more} onClick={() => handleActionsClick(user.id)}>
              {isDarkTheme ? (
                <BsThreeDotsVertical
                  size={20}
                  color="#FFFFFF"
                  style={{
                    filter: 'drop-shadow(0px 0px 1px rgba(255, 255, 255, 0.5))',
                    opacity: 0.9,
                  }}
                />
              ) : (
                <img src={more} alt="More Options" />
              )}
              {selectedItem === user.id && showModal && (
                <div className={styles.popOut} ref={popUpMenuRef}>
                  <ul className={styles.list}>
                    <li>
                      <button className={styles.list_button} onClick={() => handleEdit(user.id)}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                          }}
                        >
                          <CiEdit
                            style={{
                              fontSize: '16px',
                            }}
                          />
                          Edit User
                        </div>
                      </button>
                    </li>
                    <li>
                      <button className={styles.list_button} onClick={() => handleDelete(user.id)}>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            color: '#D14D41',
                          }}
                        >
                          <MdOutlineDeleteOutline
                            style={{
                              fontSize: '16px',
                            }}
                          />
                          Delete
                        </div>
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      {isLoading && <Spinner />}
      {allUsers && !isLoading && !error && (
        <>
          <div className={styles.usersTable}>
            <div className={styles.toolbar}>
              <div className={styles.searchWrap}>
                <FiSearch className={styles.searchIcon} />
                <input
                  type="text"
                  className={styles.search}
                  placeholder="Search name, email, organization, or role"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search users"
                />
                {search && (
                  <button
                    type="button"
                    className={styles.clearSearch}
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <FiX />
                  </button>
                )}
              </div>
              <span className={styles.resultCount}>
                {visibleUsers.length} of {allUsers.length} users
              </span>
            </div>

            <div className={styles.tableContainer}>
              <table>
                <thead>
                  <tr>
                    <th>
                      <button
                        type="button"
                        className={styles.sortable}
                        onClick={() => handleSort('name')}
                      >
                        Name {sortIcon('name')}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={styles.sortable}
                        onClick={() => handleSort('email')}
                      >
                        Email {sortIcon('email')}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={styles.sortable}
                        onClick={() => handleSort('organization')}
                      >
                        Organization {sortIcon('organization')}
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className={styles.sortable}
                        onClick={() => handleSort('role')}
                      >
                        Role {sortIcon('role')}
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.length > 0 ? (
                    renderUsers()
                  ) : (
                    <tr>
                      <td colSpan={4} className={styles.emptyState}>
                        No users match &ldquo;{search}&rdquo;. Try a different name, email, or
                        organization.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            lastPage={Math.ceil(visibleUsers.length / limitOfUsers)}
            maxLength={7}
            setCurrentPage={setCurrentPage}
          />
        </>
      )}
      {open && (
        <Modal open={open} setOpen={setOpen} edit={true} userId={selectedItem} allUsers={allUsers} />
      )}

      {openConfirm && (
        <Modal
          open={openConfirm}
          setOpen={setOpenConfirm}
          confirm={true}
          userId={selectedItem}
          allUsers={allUsers}
        />
      )}
      {error && <p>An error occurred: {error.message}</p>}
    </>
  );
}

export default UsersManagement;